import { useState, useEffect } from 'react';
import MoneraAPI from './api';
import {
  clampTerm,
  findResort,
  getAvailableTerms,
  getEligibilityQuery,
  getEligibleResorts,
  getQuotableResorts,
  getSliderOptionsKey,
  withEligibleTerms,
} from './loanConfig';
import { describeConfigError, describeQuoteError } from './quoteErrors';

/* Wait for typing to pause before requesting a new quote */
const QUOTE_DEBOUNCE_MS = 400;

/* Values the customer enters; changing any of them requires a new quote */
const INPUT_KEYS = [
  'contractType',
  'resort',
  'pricePerPoint',
  'totalPoints',
  'term',
  'downPaymentOption',
  'financeClosingCosts',
  'creditRating',
  'existingLoanPayoff',
  'deposit',
  'sellerPaysClosingCosts',
  'annualDues',
];

const debounce = (callback, wait = 0) => {
  let timeout;

  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => callback(...args), wait);
  };

  debounced.cancel = () => clearTimeout(timeout);

  return debounced;
};

const isConfigReady = (value) => !value.config.loading && !value.config.error && value.config.resorts.length > 0;

class Context {
  /* Default context value */
  value = {
    active: false,
    contractType: 'new',
    resort: '',
    pricePerPoint: null,
    totalPoints: null,
    term: null,
    termSelected: false,
    // The term the customer picked; restored when a later input qualifies for it again
    requestedTerm: null,
    downPaymentOption: 1,
    financeClosingCosts: true,
    creditRating: '-1',
    loanOption: null,
    existingLoanPayoff: null,
    deposit: 0,
    sellerPaysClosingCosts: false,
    annualDues: null,
    // Resorts and loan terms from the API
    config: {
      loading: true,
      error: null,
      resorts: [],
      supportedTerms: [],
    },
    // Terms the latest quote inputs qualify for, keyed by resort code
    eligibility: null,
    // Slider amounts from the latest quote; kept when a quote is invalidated
    sliderOptions: null,
    // API Response
    response: {
      loading: false,
      error: null,
      creditCheck: {},
      noCreditCheck: {},
    }
  }

  /* A list of subscribers to this state */
  subscribers = new Set();

  urlSyncDisableCount = 0;

  /* Identifies the latest quote request so older responses can be ignored */
  requestId = 0;

  requestController = null;

  /* Time until which quote requests are paused after a rate limit response */
  pausedUntil = 0;

  retryTimer = null;

  /* True while applying an eligibility check, whose term corrections replace the URL */
  applyingEligibility = false;

  constructor() {
    const params = new URLSearchParams(window.location.search);
    const term = Number(params.get('term'));

    this.value.contractType = (params.get('contract') === 'newcontract' || params.get('contract') === 'new' || !params.get('contract')) ? 'new' : 'refinance';
    // Validated against the API's resort list once the config loads
    this.value.resort = params.get('resort') || '';
    this.value.pricePerPoint = params.get('pricePerPoint') || null;
    this.value.totalPoints = params.get('points') || null;
    this.value.existingLoanPayoff = params.get('existingLoanPayoff') || null;
    this.value.term = term > 0 ? term : null;
    this.value.termSelected = term > 0;
    this.value.requestedTerm = this.value.term;
    this.value.downPaymentOption = Number(params.get('downPaymentOption')) || 1;
    this.value.financeClosingCosts = !params.get('financeClosingCosts') ? true : params.get('financeClosingCosts') === 'yes';
    this.value.creditRating = params.get('creditRating') || -1;
    this.value.deposit = params.get('deposit') || 0;
    this.value.annualDues = params.get('annualDues') || null;
    this.value.sellerPaysClosingCosts = params.get('sellerPaysClosingCosts') === 'yes';

    this.loadConfig();
  }

  /**
   * Get the current context value
   */
  getValue = () => ({ ...this.value });

  /**
   * Determine if the inputs have changed
   * @param {object} updated Updated value
   * @returns Boolean
   */
  inputsChanged = (old, updated) => INPUT_KEYS.some((key) => old[key] !== updated[key]);

  /**
   * Keep the resort and term within what the API config allows
   * @param {object} updated Updated context value
   * @returns {object}
   */
  applyConfig = (updated) => {
    if (!isConfigReady(updated)) {
      return updated;
    }

    const resorts = getQuotableResorts(updated.config.resorts, updated.contractType);
    const resort = findResort(resorts, updated.resort);
    const terms = getAvailableTerms(updated.config, withEligibleTerms(updated, resort));

    updated.resort = resort ? resort.name : '';

    if (terms.length) {
      updated.term = updated.termSelected
        ? clampTerm(updated.requestedTerm || updated.term, terms)
        : terms[terms.length - 1];
    }

    return updated;
  }

  /**
   * Update the URL with the inputs so that a person could copy the URL
   * @param {object} updated Updated context value
   * @param {boolean} replace Replace the current history entry instead of adding one
   * @returns void
   */
  updateURL = (updated, replace = false) => {
    if (this.urlSyncDisableCount > 0) {
      return;
    }

    const params = new URLSearchParams(window.location.search);

    params.set('contract', updated.contractType || '');
    params.set('resort', updated.resort || '');
    params.set('pricePerPoint', updated.pricePerPoint || '');
    params.set('points', updated.totalPoints || '');
    params.set('existingLoanPayoff', updated.existingLoanPayoff || '');
    params.set('term', (updated.termSelected && updated.term) || '');
    params.set('downPaymentOption', updated.downPaymentOption || '');
    params.set('financeClosingCosts', updated.financeClosingCosts ? 'yes' : 'no');
    params.set('creditRating', updated.creditRating || '');
    params.set('deposit', updated.deposit || '');
    params.set('annualDues', updated.annualDues || '');
    params.set('sellerPaysClosingCosts', updated.sellerPaysClosingCosts ? 'yes' : 'no');

    const nextUrl = new URL(window.location.href);

    nextUrl.pathname = nextUrl.pathname.replace(/^\/+/, '/');
    nextUrl.search = params.toString();

    if (nextUrl.href === window.location.href) {
      return;
    }

    try {
      window.history[replace ? 'replaceState' : 'pushState']({}, '', nextUrl.href);
    } catch (error) {
      // URL syncing is a convenience; never let it block calculator input updates.
    }
  }

  /**
   * Helper function to update global state value
   * @param {function} fn Function that accepts the current value as an argument & returns the new value
   */
  update = (fn) => {
    let updatedVal = this.applyConfig(fn(this.getValue()));

    // Check that all the requirements are there for the new contract option
    if (updatedVal.contractType === 'new') {
      if (updatedVal.resort && updatedVal.totalPoints && updatedVal.pricePerPoint) {
        updatedVal.active = true;

        if (updatedVal.sellerPaysClosingCosts) {
          updatedVal.financeClosingCosts = false;
        }
      } else {
        updatedVal.active = false;
      }
    }

    // Check that all the requirements are there for refinance option
    if (updatedVal.contractType === 'refinance') {
      if (updatedVal.resort && updatedVal.totalPoints) {
        updatedVal.active = true;
      } else {
        updatedVal.active = false;
      }
    }

    const inputsChanged = this.inputsChanged(this.value, updatedVal);
    const configLoaded = isConfigReady(updatedVal) && !isConfigReady(this.value);
    const needsQuote = updatedVal.active && isConfigReady(updatedVal) && (inputsChanged || configLoaded);

    if (inputsChanged || needsQuote || !updatedVal.active) {
      this.cancelQuote();
      updatedVal.response = { ...updatedVal.response, creditCheck: {}, noCreditCheck: {}, fallbackNotes: [] };
    }

    if (needsQuote) {
      // Mark the current results as outdated until the new quote arrives
      updatedVal.response = {
        ...updatedVal.response,
        loading: true,
        error: Date.now() < this.pausedUntil ? updatedVal.response.error : null,
      };
    }

    if (!updatedVal.active) {
      updatedVal.response = { ...updatedVal.response, loading: false };
    }

    if (inputsChanged) {
      // Corrections from loading the config shouldn't add a history entry
      this.updateURL(updatedVal, configLoaded || this.applyingEligibility);
    }

    this.value = updatedVal;

    for (const subscriber of Array.from(this.subscribers)) {
      subscriber();
    }

    if (needsQuote) {
      this.scheduleQuote();
    }
  }

  /**
   * Subscribe to the global state
   * @param {function} subscriber Function to call when the state changes
   */
  subscribe = (subscriber) => {
    this.subscribers.add(subscriber);
  }

  /**
   * Remove subscription to the global state
   * @param {function} subscriber Function to call when the state changes
   */
  unsubscribe = (subscriber) => {
    this.subscribers.delete(subscriber);
  }

  disableUrlSync = () => {
    this.urlSyncDisableCount += 1;
  }

  enableUrlSync = () => {
    this.urlSyncDisableCount = Math.max(this.urlSyncDisableCount - 1, 0);
  }

  /**
   * Load the resorts and loan terms from the API
   */
  loadConfig = async () => {
    this.update((ctx) => {
      ctx.config = { ...ctx.config, loading: true, error: null };

      return ctx;
    });

    try {
      const data = await MoneraAPI.getLoanConfig();
      const resorts = Array.isArray(data.resorts) ? data.resorts : [];

      this.update((ctx) => {
        ctx.config = {
          loading: false,
          // Without resorts the calculator can't be used; show the retry state
          error: resorts.length ? null : describeConfigError(),
          resorts,
          supportedTerms: Array.isArray(data.supportedTerms) ? data.supportedTerms : [],
        };

        return ctx;
      });
    } catch (err) {
      this.update((ctx) => {
        ctx.config = { ...ctx.config, loading: false, error: describeConfigError(err) };

        return ctx;
      });
    }
  }

  /**
   * Stop any pending or in-flight quote request
   */
  cancelQuote = () => {
    this.scheduleQuote.cancel();
    clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.requestId += 1;

    if (this.requestController) {
      this.requestController.abort();
      this.requestController = null;
    }
  }

  scheduleQuote = debounce(() => this.hydrate(), QUOTE_DEBOUNCE_MS);

  /**
   * Request a new quote right away, e.g. from a "Try again" button
   */
  retry = () => {
    if (!isConfigReady(this.value)) {
      this.loadConfig();
      return;
    }

    this.update((ctx) => {
      ctx.response = { ...ctx.response, loading: true, error: null };

      return ctx;
    });

    this.hydrate();
  }

  /**
   * Hydrate response with data from the API
   */
  hydrate = async () => {
    if (!this.value.active || !isConfigReady(this.value)) {
      return;
    }

    // While rate limited, the retry timer requests the latest inputs when the pause ends
    if (Date.now() < this.pausedUntil) {
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => this.hydrate(), this.pausedUntil - Date.now());
      return;
    }

    this.cancelQuote();

    const requestId = this.requestId;
    const controller = new AbortController();
    this.requestController = controller;

    // Cap the term at what these inputs qualify for before quoting it
    const eligibilityQuery = getEligibilityQuery(this.value);
    const eligibilityKey = eligibilityQuery && JSON.stringify(eligibilityQuery);

    if (eligibilityKey && (!this.value.eligibility || this.value.eligibility.key !== eligibilityKey)) {
      let resorts = {};

      try {
        resorts = getEligibleResorts(await MoneraAPI.getLoanConfig(eligibilityQuery, controller.signal));
      } catch (err) {
        if (requestId !== this.requestId || err.name === 'AbortError') {
          return;
        }
        // Quote the configured terms instead; the API still enforces eligibility
      }

      if (requestId !== this.requestId) {
        return;
      }

      // A shorter eligible term changes the inputs, which schedules its own quote
      this.applyingEligibility = true;

      try {
        this.update((ctx) => {
          ctx.eligibility = { key: eligibilityKey, resorts };

          return ctx;
        });
      } finally {
        this.applyingEligibility = false;
      }

      if (requestId !== this.requestId) {
        return;
      }
    }

    const resort = findResort(this.value.config.resorts, this.value.resort);
    const sliderOptionsKey = getSliderOptionsKey(this.value);

    const body = {
      type: this.value.contractType,
      resort: resort ? resort.code : this.value.resort,
      totalPoints: Number(this.value.totalPoints),
      term: Number(this.value.term),
      creditRating: this.value.creditRating > -1 ? Number(this.value.creditRating) : undefined,
    }

    if (this.value.contractType === 'new') {
      body.pricePerPoint = Number(this.value.pricePerPoint);
      body.downPayment = this.value.downPaymentOption;
      body.financeClosingCosts = this.value.financeClosingCosts;
      body.sellerPaysClosingCosts = this.value.sellerPaysClosingCosts;
      body.deposit = Number(this.value.deposit) || 0;
      if (this.value.annualDues != null && this.value.annualDues !== '') {
        body.annualDues = Number(this.value.annualDues);
      }
    } else {
      body.loanOption = this.value.downPaymentOption;
      body.existingLoanPayoff = Number(this.value.existingLoanPayoff) || 0;
    }

    try {
      const data = await MoneraAPI.calculateClosingCosts(body, controller.signal);

      if (requestId !== this.requestId) {
        return;
      }

      this.requestController = null;

      this.update((ctx) => {
        ctx.response = {
          loading: false,
          error: null,
          creditCheck: data.credit_check,
          noCreditCheck: data.no_credit_check,
        };
        ctx.sliderOptions = {
          key: sliderOptionsKey,
          downPayment: (data.no_credit_check || {}).down_payment_options,
          refinance: (data.no_credit_check || {}).refinance_options,
        };

        return ctx;
      });
    } catch (err) {
      if (requestId !== this.requestId || err.name === 'AbortError') {
        return;
      }

      this.requestController = null;

      if (err.status === 429) {
        this.pausedUntil = Date.now() + err.retryAfterSeconds * 1000;
        clearTimeout(this.retryTimer);
        this.retryTimer = setTimeout(() => this.hydrate(), err.retryAfterSeconds * 1000);
      }

      this.update((ctx) => {
        ctx.response = { ...ctx.response, loading: false, error: describeQuoteError(err), fallbackNotes: err.fallbackNotes || [] };

        return ctx;
      });
    }
  }
}

/**
 * Create a global context instance for the app to use
 */
const ctx = new Context();

/**
 * Custom React hook to allow for re-renders to occur when the global context changes
 */
export default function useContext() {
  const [state, setState] = useState(ctx.value);

  const triggerRender = () => setState(ctx.getValue());

  useEffect(() => {
    ctx.subscribe(triggerRender);

    return () => ctx.unsubscribe(triggerRender);
  }, []);

  return [state, ctx.update];
}

export function disableUrlSync() {
  ctx.disableUrlSync();
}

export function enableUrlSync() {
  ctx.enableUrlSync();
}

export function retryQuote() {
  ctx.retry();
}



// WEBPACK FOOTER //
// blocks/quote-calculator/app/context.js